import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Image, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView, SettingsItem, SettingsItemContent, SettingsItemDescription, SettingsItemTitle, Spacer, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { acceptAutosaveMandate } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectFeatureFlag } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { formatSortCode } from 'utils/formatting';
import Modal from 'utils/packages/Modal';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
const directDebitLogo = require('../../img/directDebitLogo.png');
const AutosaveConfirmBankDetails = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const [checked, setChecked] = useState(false);
    const [, , requesting, fetch] = useFetchOld(muteErrorAndResult);
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const onCheckPress = useCallback(() => {
        setChecked((oldValue) => !oldValue);
    }, []);
    const directDebitFlag = useAppSelector((store) => selectFeatureFlag(store, 'direct_debit_provider'));
    const transactionMemo = useMemo(() => {
        if (directDebitFlag.extra?.providerName &&
            directDebitFlag.extra?.providerPrivacyUrl &&
            directDebitFlag.extra?.transactionMemo) {
            return directDebitFlag.extra.transactionMemo;
        }
        return 'EMMA APP TRANSFER';
    }, [
        directDebitFlag.extra?.providerName,
        directDebitFlag.extra?.providerPrivacyUrl,
        directDebitFlag.extra?.transactionMemo,
    ]);
    const { bankAccount } = route.params;
    const email = useAppSelector((state) => state.user.user.email);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const sortCode = useMemo(() => {
        const selected = 'ukSortCode' in bankAccount ? bankAccount.ukSortCode : bankAccount.sortCode ?? '';
        return selected.replace(/-/g, '');
    }, [bankAccount]);
    const accountNumber = useMemo(() => ('ukAccountNumber' in bankAccount ? bankAccount.ukAccountNumber : bankAccount.accountNumber ?? ''), [bankAccount]);
    const handleContinue = useCallback(async () => {
        if ('onDirectDebitSetup' in route.params && route.params.onDirectDebitSetup) {
            const response = await fetch(acceptAutosaveMandate(sortCode, accountNumber));
            if (response?.mandateStatus === 'ACTIVE' || response?.mandateStatus === 'PENDING') {
                navigation.pop(2);
                route.params.onDirectDebitSetup?.();
            }
            else {
                Modal.showConfirmation({
                    title: 'We couldn’t set up your Direct Debit',
                    subtitle: 'Please try again or contact support.',
                    confirmButtonTitle: 'Okay',
                });
            }
            return;
        }
        if (!('rules' in route.params)) {
            dispatch({
                type: 'SAVE_ONBOARDING_STATE',
                fundingAccount: bankAccount,
            });
            navigateToNextScreen();
            return;
        }
        navigation.navigateDeprecated('AutosaveReviewInfo', {
            rules: route.params.rules,
            overdraft: route.params.overdraft,
            bankAccount: route.params.bankAccount,
            distribution: route.params.distribution,
            collectionFrequency: route.params.collectionFrequency,
        });
    }, [route.params, sortCode, accountNumber, bankAccount, navigateToNextScreen]);
    const formattedSortCodeAndAccountNumber = useMemo(() => {
        if (bankAccount) {
            return `${formatSortCode(sortCode)} ${accountNumber}`;
        }
        return '';
    }, [accountNumber, bankAccount, sortCode]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Check your details are correct"/>
          <CardView noMarginTop>
            <SettingsItem>
              <SettingsItemContent>
                <SettingsItemDescription>Account</SettingsItemDescription>
                <SettingsItemTitle>
                  {'name' in bankAccount && bankAccount.name && bankAccount.bankName
            ? `${bankAccount.bankName} · ${bankAccount.name}`
            : bankAccount.bankName || 'Linked bank'}
                </SettingsItemTitle>
              </SettingsItemContent>
            </SettingsItem>
            <SettingsItem>
              <SettingsItemContent>
                <SettingsItemDescription>Sort code and account number</SettingsItemDescription>
                <SettingsItemTitle>{formattedSortCodeAndAccountNumber}</SettingsItemTitle>
              </SettingsItemContent>
            </SettingsItem>
          </CardView>

          <CardView withPadding noMarginTop>
            <Text TextThin-14 Secondary>
              Payments will show as “{transactionMemo}” on your bank statement. After you complete the setup, an email
              confirmation will be sent to {email} within 3 days.
            </Text>
            <Spacer.H16 />
            <View style={styles.footer}>
              <View style={styles.flex}>
                <Text TextThin-14 Secondary>
                  You may cancel this Direct Debit at any time by contacting Emma or your bank.
                </Text>
              </View>
              <Spacer.W16 />
              <Image source={directDebitLogo} style={styles.img}/>
            </View>
          </CardView>

          <TouchableBounceCard onPress={onCheckPress} onDark withPadding noMarginTop>
            <View style={styles.row}>
              <RadioButton type="multiple" isChecked={checked}/>
              <Spacer.W16 />
              <View style={styles.flex}>
                <Text TextThin-14>
                  I confirm that I am the account holder and am authorised to set up Direct Debit payments on this
                  account.
                </Text>
              </View>
            </View>
          </TouchableBounceCard>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brand title="Continue" isFetching={requesting} onPress={handleContinue} disabled={!checked}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    footer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    img: {
        width: rem(80),
        height: rem(80),
        tintColor: colors.text.primary,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
}));
export default AutosaveConfirmBankDetails;
